import React, { useState, useRef, useEffect, useMemo } from "react";
import { searchTireSizes, isValidTireSizeFormat, isInMasterList, groupByDiameter } from "../../utils/tireSizeParser";
import styles from "./VehicleTypeahead.module.css";
import { isTouchPhone } from "../../utils/phoneKeyboard";

export interface YMMVMatch {
  year: string;
  make: string;
  model: string;
  display: string;
  tire_sizes: string[];
}

export interface YMMVGroup {
  label: string;
  yearRange: string;
  count: number;
  variant?: string;
  searchQuery?: string;
  // TRAC-443: model (vehicle) rows carry structured year/make/model so a click
  // can advance straight to the tire-size picker, plus hasVariants so the
  // handler can pick advance-vs-narrow. Absent on year/make/family drill rows.
  year?: string;
  make?: string;
  model?: string;
  hasVariants?: boolean;
}

export interface TireSizeOption {
  size: string;
  is_factory: boolean;
  variants: string[];
  shop_url?: string;
}

export interface StaggeredPair {
  front_size: string;
  rear_size: string;
  is_factory: boolean;
  shop_url?: string;
}

export interface TireSizeData {
  display: string;
  tire_sizes: TireSizeOption[];
  variants: { name: string; sizes: { size: string; is_factory: boolean }[] }[];
  staggered_pairs?: StaggeredPair[];
}

interface VehicleTypeaheadProps {
  results: YMMVMatch[];
  groups: YMMVGroup[] | null;
  isLoading: boolean;
  query: string;
  selectedIndex: number;
  totalCount: number;
  groupType: string | null;
  multiTier: boolean;
  totalMatches: number;
  droppedYear: string | null;
  // Tire size step props
  step: "vehicle" | "tire_size";
  tireSizeData: TireSizeData | null;
  tireSizeLoading: boolean;
  tireSizeError?: boolean;
  selectedVehicle: YMMVMatch | null;
  allTireSizes: string[] | null;
  onSelect: (match: YMMVMatch) => void;
  onGroupSelect: (group: YMMVGroup) => void;
  onTireSizeSelect: (vehicle: YMMVMatch, tireSize: string, shopUrl?: string) => void;
  onTireSizeRetry?: () => void;
  onBackToVehicle: () => void;
  // TRAC-443: step back one drill-down level in the vehicle search; canGoBack
  // gates the button so it only shows once the user has clicked into a group.
  onStepBack?: () => void;
  canGoBack?: boolean;
  onDismiss: () => void;
  embedMode?: boolean;
  isPreConversation?: boolean;
}

/**
 * Highlight matching query tokens in a label at the character level.
 * Matched substrings render bright, everything else renders dim.
 */
/**
 * Map the backend's `group_type` (returned from /api/ymmv) to the
 * interaction-tracking eventType. TRAC-527: each drill-down step gets its
 * own impression so the analytics side can see what the user was choosing
 * from at year / make / model-family stage.
 */
function groupTypeToEventType(groupType: string | null): string {
  switch (groupType) {
    case 'year': return 'ymmv_year';
    case 'make': return 'ymmv_make';
    // Backend uses both 'model_family' (early narrowing step) and 'model'
    // (multi-tier model+variant step). Both map to the same event type
    // so dashboards can aggregate them as "model step".
    case 'model_family':
    case 'model':
      return 'ymmv_model_family';
    default: return 'ymmv_vehicle';
  }
}

// Exported for the partial YMMV card's option preview (TRAC-896): the card
// shows the same rows this picker will open with, so it renders them with the
// same matched-vs-dimmed treatment rather than approximating it.
export function highlightLabel(label: string, query: string): React.ReactNode {
  if (!query.trim()) return label;

  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return label;

  // Mark matched character positions by consuming tokens left-to-right: each
  // token matches at or after the end of the previous token's match. This
  // highlights the typed prefix in order (typing "2023 BMW 3" lights up
  // "2023 BMW 3", NOT every "3" in "330e") while still catching a standalone
  // "M" later in the label — the "m" token is found after the earlier "BMW"
  // match, so the standalone word highlights without re-matching the M in BMW.
  const matched: boolean[] = new Array(label.length).fill(false);
  const labelLower = label.toLowerCase();

  let searchFrom = 0;
  for (const token of tokens) {
    const idx = labelLower.indexOf(token, searchFrom);
    if (idx === -1) continue; // not found in order — leave it un-highlighted
    for (let i = idx; i < idx + token.length; i++) matched[i] = true;
    searchFrom = idx + token.length;
  }

  // Build spans from contiguous matched/unmatched runs
  const parts: React.ReactNode[] = [];
  let i = 0;
  while (i < label.length) {
    const isMatch = matched[i];
    let j = i;
    while (j < label.length && matched[j] === isMatch) j++;
    const text = label.slice(i, j);
    parts.push(
      isMatch ? (
        <span key={i}>{text}</span>
      ) : (
        <span key={i} className={styles.dimWord}>{text}</span>
      )
    );
    i = j;
  }

  return parts;
}


// ---------------------------------------------------------------------------
// ComboInput — searchable select with multi-column grid flyout
// ---------------------------------------------------------------------------

interface ComboInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
}

const ComboInput: React.FC<ComboInputProps> = ({ label, value, onChange, suggestions, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);

  const filtered = filter
    ? suggestions.filter((s) => s.startsWith(filter))
    : suggestions;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-focus the filter input when flyout opens
  useEffect(() => {
    if (open) setTimeout(() => filterRef.current?.focus(), 50);
  }, [open]);

  return (
    <div className={styles.comboInputContainer} ref={containerRef}>
      <label className={styles.comboInputLabel}>{label}</label>
      <button
        className={`${styles.comboInputField} ${value ? "" : styles.comboInputPlaceholder}`}
        onClick={() => { setOpen(!open); setFilter(""); }}
        type="button"
      >
        {value || placeholder || "—"}
      </button>

      {open && (
        <div className={styles.comboFlyout}>
          <div className={styles.comboFlyoutHeader}>
            <span className={styles.comboFlyoutTitle}>{label}</span>
            <button
              className={styles.comboFlyoutClose}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); setFilter(""); }}
            >
              &times;
            </button>
          </div>
          <input
            ref={filterRef}
            type="text"
            className={styles.comboFlyoutFilter}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            autoComplete="off"
          />
          <div className={styles.comboFlyoutGrid}>
            {filtered.map((s) => (
              <button
                key={s}
                className={`${styles.comboFlyoutItem} ${s === value ? styles.comboFlyoutItemActive : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(s);
                  setOpen(false);
                  setFilter("");
                }}
              >
                {s}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className={styles.comboFlyoutEmpty}>No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// TireSizePicker — step 2 of the YMMV flow
// ---------------------------------------------------------------------------

interface TireSizePickerProps {
  vehicle: YMMVMatch;
  data: TireSizeData | null;
  loading: boolean;
  error?: boolean;
  allTireSizes: string[] | null;
  onSelect: (vehicle: YMMVMatch, tireSize: string, shopUrl?: string) => void;
  onRetry?: () => void;
  onBack: () => void;
  onDismiss: () => void;
  embedMode?: boolean;
  isPreConversation?: boolean;
}

const TireSizePicker: React.FC<TireSizePickerProps> = ({
  vehicle,
  data,
  loading,
  error,
  allTireSizes,
  onSelect,
  onRetry,
  onBack,
  onDismiss,
  embedMode,
  isPreConversation,
}) => {
  const containerClass = `${styles.container}${embedMode ? ` ${styles.embedContainer}` : ''}${embedMode && isPreConversation ? ` ${styles.embedContainerPreConversation}` : ''}`;
  const [customOpen, setCustomOpen] = useState(false);
  const [customQuery, setCustomQuery] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);
  // TRAC-1175 (keyboard rule 25): set by a tap on "Something Different". The box that
  // opens by itself for a vehicle with no sizes does not raise the keyboard on a phone.
  const customTappedRef = useRef(false);

  const staggeredPairs = data?.staggered_pairs || [];
  // Sizes that are part of a staggered pair — exclude from individual factory list
  const staggeredSizeSet = new Set(
    staggeredPairs.flatMap((p) => [p.front_size, p.rear_size])
  );
  const factorySizes = data?.tire_sizes.filter(
    (ts) => ts.is_factory && !staggeredSizeSet.has(ts.size)
  ) || [];
  const optionalSizes = data?.tire_sizes.filter((ts) => !ts.is_factory) || [];
  const noSizes = data && data.tire_sizes.length === 0 && staggeredPairs.length === 0;

  // Collect unique variant names across all sizes — display in banner, not per-row
  const variantNames = useMemo(() => {
    if (!data) return [];
    const names = new Set<string>();
    for (const ts of data.tire_sizes) {
      for (const v of ts.variants) names.add(v);
    }
    return Array.from(names).sort();
  }, [data]);

  // Auto-expand custom input when no sizes available
  useEffect(() => {
    if (noSizes) { customTappedRef.current = false; setCustomOpen(true); }
  }, [noSizes]);

  // Focus custom input when opened
  useEffect(() => {
    if (!customOpen) return;
    if (!customTappedRef.current && isTouchPhone()) return;
    setTimeout(() => customInputRef.current?.focus(), 50);
  }, [customOpen]);

  // Build filtered suggestions using smart parser (excluding vehicle's own sizes)
  const vehicleSizeSet = useMemo(
    () => new Set(data?.tire_sizes.map((ts) => ts.size) || []),
    [data],
  );
  const customResults = useMemo(
    () => searchTireSizes(customQuery, allTireSizes, vehicleSizeSet),
    [customQuery, allTireSizes, vehicleSizeSet],
  );
  const customGroups = useMemo(
    () => groupByDiameter(customResults),
    [customResults],
  );
  const showGrouped = customGroups.length >= 1;

  const customSizeValid = isValidTireSizeFormat(customQuery.trim());
  const customSizeInCatalog = customSizeValid && isInMasterList(customQuery.trim(), allTireSizes);

  return (
    // data-ymmv-interface: handle for the TRAC-896 open morph's content stagger
    // (utils/ymmvMorph.ts animates this root ~150ms behind the panel). Purely a
    // lookup hook — nothing about the component depends on it.
    <div className={containerClass} data-ymmv-interface="tire-size">
      {/* Header */}
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={onBack}
          aria-label="Back to vehicle search"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <div className={styles.title}>Select Your Tire Size</div>
        <button
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Dismiss"
          title="Dismiss"
        >
          &times;
        </button>
      </div>

      {/* Vehicle banner — TRAC-443: variant shown as a chip badge ("pill") */}
      <div className={styles.vehicleBanner}>
        <div className={styles.vehicleBannerLeft}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {variantNames.length === 0 ? (
            <span>{vehicle.display}</span>
          ) : (
            <span className={styles.variantPill}>
              <span>{vehicle.display}</span>
              <span className={styles.variantChip}>{variantNames.join(", ")}</span>
            </span>
          )}
        </div>
      </div>

      {/* Tire size list */}
      <div
        className={customOpen ? styles.tireSizeListCustomExpanded : styles.tireSizeList}
        data-track-id="ymmv_tire_size"
        data-track-turn={-1}
      >
        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Loading tire sizes...</span>
          </div>
        )}

        {!loading && error && (
          <div className={styles.tireSizeEmpty}>
            <span>Unable to load tire sizes. Please try again.</span>
            {onRetry && (
              <button className={styles.retryButton} onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        )}

        {!loading && !error && !customOpen && noSizes && (
          <div className={styles.tireSizeEmpty}>
            No standard tire sizes found for this vehicle. Enter your size below.
          </div>
        )}

        {/* Staggered factory pairs — front/rear displayed together */}
        {!loading && !customOpen && staggeredPairs.filter((p) => p.is_factory).length > 0 && (
          <>
            <div className={styles.tireSizeSectionLabel}>Factory Tire Size</div>
            {staggeredPairs.filter((p) => p.is_factory).map((pair) => (
              <button
                key={`${pair.front_size}-${pair.rear_size}`}
                className={styles.staggeredPairItem}
                onClick={() => onSelect(vehicle, `${pair.front_size} (front) / ${pair.rear_size} (rear)`, pair.shop_url)}
                data-track-option={`${pair.front_size} (front) / ${pair.rear_size} (rear)`}
                data-track-extra={JSON.stringify({ kind: 'factory_staggered' })}
              >
                <div className={styles.staggeredPairRow}>
                  <div className={styles.staggeredSide}>
                    <span className={styles.staggeredLabel}>Front</span>
                    <span className={styles.staggeredSize}>{pair.front_size}</span>
                  </div>
                  <div className={styles.staggeredSide}>
                    <span className={styles.staggeredLabel}>Rear</span>
                    <span className={styles.staggeredSize}>{pair.rear_size}</span>
                  </div>
                </div>
                <span className={styles.tireSizeBadge}>Factory</span>
              </button>
            ))}
          </>
        )}

        {/* Non-staggered factory sizes */}
        {!loading && !customOpen && factorySizes.length > 0 && (
          <>
            {staggeredPairs.filter((p) => p.is_factory).length === 0 && (
              <div className={styles.tireSizeSectionLabel}>Factory Sizes</div>
            )}
            {factorySizes.map((ts) => (
              <button
                key={ts.size}
                className={styles.tireSizeItem}
                onClick={() => onSelect(vehicle, ts.size, ts.shop_url)}
                data-track-option={ts.size}
                data-track-extra={JSON.stringify({ kind: 'factory' })}
              >
                <span className={styles.tireSizeValue}>{ts.size}</span>
                <span className={styles.tireSizeBadge}>Factory</span>
              </button>
            ))}
          </>
        )}

        {!loading && !customOpen && optionalSizes.length > 0 && (
          <>
            <div className={styles.tireSizeSectionLabel}>Optional Sizes</div>
            {optionalSizes.map((ts) => (
              <button
                key={ts.size}
                className={styles.tireSizeItem}
                onClick={() => onSelect(vehicle, ts.size, ts.shop_url)}
                data-track-option={ts.size}
                data-track-extra={JSON.stringify({ kind: 'optional' })}
              >
                <span className={styles.tireSizeValue}>{ts.size}</span>
              </button>
            ))}
          </>
        )}

        {/* Something Different — free-form tire size search */}
        {!loading && !error && (
          <div className={customOpen ? styles.customSectionExpanded : styles.customSection}>
            <button
              className={styles.customToggle}
              onClick={() => { customTappedRef.current = true; setCustomOpen(!customOpen); }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: customOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              Something Different
            </button>

            {customOpen && (
              <div className={styles.customInputAreaExpanded}>
                <input
                  ref={customInputRef}
                  type="text"
                  className={styles.customSizeInput}
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && customSizeValid) {
                      onSelect(vehicle, customQuery.trim());
                    }
                  }}
                  placeholder="Type a tire size, e.g. 225/50R17"
                  autoComplete="off"
                />
                {customResults.length > 0 && (
                  <div className={styles.customSizeResultsExpanded}>
                    {showGrouped ? (
                      customGroups.map((group) => (
                        <React.Fragment key={group.diameter}>
                          <div className={styles.tireSizeSectionLabel}>R{group.diameter}</div>
                          {group.sizes.map((r) => (
                            <button
                              key={r.size}
                              className={styles.tireSizeItem}
                              onClick={() => onSelect(vehicle, r.size)}
                              data-track-option={r.size}
                              data-track-extra={JSON.stringify({ kind: 'custom_grouped' })}
                            >
                              <span className={styles.tireSizeValue}>
                                {highlightLabel(r.size, customQuery)}
                              </span>
                            </button>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      customResults.map((r) => (
                        <button
                          key={r.size}
                          className={styles.tireSizeItem}
                          onClick={() => onSelect(vehicle, r.size)}
                          data-track-option={r.size}
                          data-track-extra={JSON.stringify({ kind: 'custom_result' })}
                        >
                          <span className={styles.tireSizeValue}>
                            {highlightLabel(r.size, customQuery)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {customQuery.trim() && !customSizeValid && customResults.length === 0 && (
                  <div className={styles.customSizeHint}>
                    Enter a valid tire size (e.g. 225/50R17 or 33x12.5R15)
                  </div>
                )}
                {customSizeValid && (
                  <button
                    className={styles.customSubmit}
                    onClick={() => onSelect(vehicle, customQuery.trim())}
                    data-track-option={customQuery.trim()}
                    data-track-extra={JSON.stringify({
                      kind: 'custom_submit',
                      inCatalog: customSizeInCatalog,
                    })}
                  >
                    Use {customQuery.trim()}
                    {!customSizeInCatalog && (
                      <span className={styles.customSubmitHint}> (not in our catalog)</span>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// VehicleTypeahead — main component (step 1: vehicle search)
// ---------------------------------------------------------------------------

const VehicleTypeahead: React.FC<VehicleTypeaheadProps> = ({
  results,
  groups,
  isLoading,
  query,
  selectedIndex,
  totalCount,
  groupType,
  multiTier,
  totalMatches,
  droppedYear,
  step,
  tireSizeData,
  tireSizeLoading,
  tireSizeError,
  selectedVehicle,
  allTireSizes,
  onSelect,
  onGroupSelect,
  onTireSizeSelect,
  onTireSizeRetry,
  onBackToVehicle,
  onStepBack,
  canGoBack,
  onDismiss,
  embedMode,
  isPreConversation,
}) => {
  const containerClass = `${styles.container}${embedMode ? ` ${styles.embedContainer}` : ''}${embedMode && isPreConversation ? ` ${styles.embedContainerPreConversation}` : ''}`;

  // Step 2: Tire size picker
  if (step === "tire_size" && selectedVehicle) {
    return (
      <TireSizePicker
        vehicle={selectedVehicle}
        data={tireSizeData}
        loading={tireSizeLoading}
        error={tireSizeError}
        allTireSizes={allTireSizes}
        onSelect={onTireSizeSelect}
        onRetry={onTireSizeRetry}
        onBack={onBackToVehicle}
        onDismiss={onDismiss}
        embedMode={embedMode}
        isPreConversation={isPreConversation}
      />
    );
  }

  // Step 1: Vehicle search (existing behavior)
  const hasGroups = groups != null && groups.length > 0;
  const hasResults = results.length > 0 || hasGroups;

  return (
    // data-ymmv-interface: handle for the TRAC-896 open morph's content stagger
    // — this is the root the card morphs INTO, faded/slid in ~150ms behind the
    // growing panel. See utils/ymmvMorph.ts.
    <div className={containerClass} data-ymmv-interface="vehicle">
      <div className={styles.header}>
        {/* TRAC-443: Back steps up one drill-down level (year → make → model)
            without making the user hand-delete text. Always rendered so the
            header layout never shifts — disabled + greyed until the user has
            clicked into a group. */}
        <button
          className={styles.backButton}
          onClick={onStepBack}
          disabled={!canGoBack}
          aria-label="Back to previous step"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <div className={styles.title}>Find Your Vehicle</div>
        <button
          className={styles.dismissButton}
          onClick={onDismiss}
          aria-label="Dismiss vehicle search"
          title="Dismiss"
        >
          &times;
        </button>
      </div>

      <div className={styles.hint}>
        Type your year, make, or model below to search
      </div>

      <div className={styles.resultsList}>
        {isLoading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Searching...</span>
          </div>
        )}

        {!isLoading && hasResults && droppedYear && (
          <div className={styles.droppedYearBanner}>
            We don&rsquo;t have any {droppedYear} vehicles. Showing matches without the year:
          </div>
        )}

        {!isLoading && !hasResults && query.trim().length > 0 && (
          <div className={styles.emptyState}>
            {/\b(19|20)\d{2}\b/.test(query) ? (
              <>No vehicles match &ldquo;{query}&rdquo;. Check the year, or try a different make and model.</>
            ) : (
              <>No vehicles found for &ldquo;{query}&rdquo;. Try a different search.</>
            )}
          </div>
        )}

        {!isLoading && !hasResults && query.trim().length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            Start typing to search for your vehicle
          </div>
        )}

        {!isLoading && hasGroups && (
          // TRAC-527: one impression per drill-down step. The backend reuses
          // groupType='make' for both the make list AND the variant list under
          // a chosen make, so keying on groupType alone misses the second step
          // (impression dedup'd, both clicks share one sortKey). Include the
          // first label as a step signature — it changes on every drill-down
          // (e.g. "2024 Acura" → "2024 Audi A3 FWD") so React remounts and IO
          // fires a fresh impression with the new options_shown.
          <div
            // TRAC-527: fingerprint the full label set in the React key so
            // each drill-down step (year → make-list → model-sublist →
            // variant) is a fresh DOM element. ChatTrackingRoot's
            // element-identity cache then fires a fresh impression per
            // step with the right options_shown, even when the backend
            // reuses groupType across steps (e.g. groupType='make' for
            // both make-list and model-sublist). Using just first label +
            // length leaves a thin collision window if two consecutive
            // drill-downs happen to start with the same label and have
            // matching counts; the joined fingerprint eliminates it.
            key={`ymmv-group-${groupType}:${groups!.map((g) => g.label).join('|')}`}
            data-track-id={groupTypeToEventType(groupType)}
            data-track-turn={-1}
            // TRAC-527: IntersectionObserver needs a real layout box, so we can't
            // use `display: contents` (the make/model impressions were dropped).
            // Mirror the parent `.resultsList` direction so JSX order surfaces
            // the same way it did when buttons were direct children.
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {groups!.map((group, idx) => {
              // TRAC-443: variant groups (group.variant set) render as a chip
              // badge ("pill"). Non-variant groups (year/make/model drill-downs)
              // keep the simple label render.
              const isVariantGroup = !!group.variant && !!group.searchQuery;
              return (
                <button
                  key={idx}
                  className={`${styles.resultItem} ${styles.groupItem} ${
                    isVariantGroup ? styles.variantPillRow : ""
                  } ${
                    idx === selectedIndex ? styles.resultItemActive : ""
                  }`}
                  onClick={() => onGroupSelect(group)}
                  data-track-option={group.label}
                  data-track-extra={JSON.stringify({ kind: 'group', groupType: groupType })}
                >
                  {isVariantGroup ? (
                    <span className={styles.variantPill}>
                      <span className={styles.resultLabel}>{highlightLabel(group.searchQuery!, query)}</span>
                      <span className={styles.variantChip}>{group.variant}</span>
                    </span>
                  ) : (
                    <span className={styles.resultLabel}>{highlightLabel(group.label, query)}</span>
                  )}
                  {!multiTier && (
                    <span className={styles.resultMeta}>
                      {group.yearRange} &middot; {group.count} variant
                      {group.count !== 1 ? "s" : ""}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {!isLoading && hasResults && !hasGroups && (
          <div
            key="ymmv-matches"
            data-track-id="ymmv_vehicle"
            data-track-turn={-1}
            // TRAC-527: see groups wrapper above — mirror the parent direction
            // so the wrapper doesn't double-flip JSX order.
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            {results.map((match, idx) => (
              <button
                key={idx}
                className={`${styles.resultItem} ${
                  idx === selectedIndex ? styles.resultItemActive : ""
                }`}
                onClick={() => onSelect(match)}
                data-track-option={match.display}
                data-track-extra={JSON.stringify({ kind: 'match' })}
              >
                <span className={styles.resultLabel}>{highlightLabel(match.display, query)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default VehicleTypeahead;
